/**
 * Utility functions for Google Fonts integration
 */

/**
 * Format font name for Google Fonts URL (replace spaces with +)
 * @param {string} fontName - Font family name
 * @returns {string} URL-formatted font name
 */
export const formatFontNameForUrl = fontName => {
  if (!fontName || typeof fontName !== 'string') return '';
  return fontName.trim().replace(/\s+/g, '+');
};

/**
 * Generate Google Fonts URL for a single font family
 * @param {Object} fontSpec - Font specification
 * @param {string} fontSpec.family - Font family name
 * @param {number[]} fontSpec.weights - Array of font weights
 * @param {boolean} fontSpec.hasItalic - Whether italic variants are needed
 * @param {boolean} fontSpec.hasRegular - Whether regular variants are needed
 * @returns {string} Google Fonts family parameter
 */
export const generateFontFamilyParam = ({
  family,
  weights = [],
  hasItalic = false,
  hasRegular = true,
}) => {
  if (!family) return '';

  const formattedName = formatFontNameForUrl(family);

  const sortedWeights = [...new Set(weights)].sort((a, b) => a - b);

  // Build weight specifications
  const weightSpecs = [];

  if (hasRegular && hasItalic) {
    // Both regular and italic needed but no weights specified
    if (sortedWeights.length === 0) {
      return `family=${formattedName}:ital@0;1`;
    }

    // Both regular and italic needed: ital,wght@0,400;0,700;1,400;1,700
    sortedWeights.forEach(weight => {
      weightSpecs.push(`0,${weight}`); // Regular
      weightSpecs.push(`1,${weight}`); // Italic
    });

    return `family=${formattedName}:ital,wght@${weightSpecs.join(';')}`;
  } else if (hasItalic) {
    // Only italic needed but no weights specified
    if (sortedWeights.length === 0) {
      return `family=${formattedName}:ital`;
    }

    // Only italic needed: ital,wght@1,400;1,700
    sortedWeights.forEach(weight => {
      weightSpecs.push(`1,${weight}`);
    });
    return `family=${formattedName}:ital,wght@${weightSpecs.join(';')}`;
  } else {
    if (sortedWeights.length === 0) {
      return `family=${formattedName}`;
    }

    // Only regular needed: wght@400;700
    return `family=${formattedName}:wght@${sortedWeights.join(';')}`;
  }
};

/**
 * Generate complete Google Fonts URL for multiple font families
 * @param {Object[]} fonts - Array of font specifications
 * @returns {string} Complete Google Fonts CSS URL
 */
export const generateGoogleFontsUrl = (fonts = []) => {
  if (!Array.isArray(fonts) || fonts.length === 0) return '';

  const validFonts = fonts.filter(
    font => font && font.family && typeof font.family === 'string' && font.family.trim() !== '',
  );

  if (validFonts.length === 0) return '';

  const familyParams = validFonts.map(generateFontFamilyParam).filter(param => param !== '');

  if (familyParams.length === 0) return '';

  const baseUrl = 'https://fonts.googleapis.com/css2';
  const queryParams = [...familyParams, 'display=swap'];

  return `${baseUrl}?${queryParams.join('&')}`;
};

/**
 * Build the canonical name of the metric-adjusted fallback family for a given primary font.
 * The server side emits an @font-face with this exact name so the browser can use it
 * while the real font is loading, matching its metrics to avoid CLS during swap.
 * @param {string} primaryFont - Primary font family name
 * @returns {string} Name of the adjusted fallback family (without quotes)
 */
export const getAdjustedFallbackName = primaryFont => {
  if (!primaryFont) return '';
  return `${primaryFont} Fallback`;
};

/**
 * Build CSS font-family string with proper fallbacks
 * @param {string} primaryFont - Primary font family name
 * @param {string[]} fallbacks - Array of fallback fonts
 * @param {Object} [options]
 * @param {boolean} [options.includeAdjustedFallback=true] - Whether to inject the
 *   metric-adjusted "<Family> Fallback" reference right after the primary family.
 *   The actual @font-face for this name is emitted server-side via googleFontsServer.
 * @returns {string} Complete font-family CSS value
 */
export const buildFontFamilyWithFallbacks = (
  primaryFont,
  fallbacks = [],
  { includeAdjustedFallback = true } = {},
) => {
  const defaultFallbacks = [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    'Oxygen-Sans',
    'Ubuntu',
    'Cantarell',
    '"Helvetica Neue"',
    'sans-serif',
  ];

  if (!primaryFont) {
    return defaultFallbacks.join(', ');
  }

  const adjustedFallback = includeAdjustedFallback
    ? [`"${getAdjustedFallbackName(primaryFont)}"`]
    : [];

  const allFonts = [`"${primaryFont}"`, ...adjustedFallback, ...fallbacks, ...defaultFallbacks];

  // Remove duplicates while preserving order
  const uniqueFonts = [];
  const seen = new Set();

  allFonts.forEach(font => {
    const normalizedFont = font.toLowerCase().replace(/["']/g, '');
    if (!seen.has(normalizedFont)) {
      seen.add(normalizedFont);
      uniqueFonts.push(font);
    }
  });

  return uniqueFonts.join(', ');
};

/**
 * Parse a Google Fonts CSS response into individual @font-face blocks.
 * Extracts the font-family, src woff2 URL, font-weight, font-style, and unicode-range
 * so callers can emit targeted <link rel="preload"> for the latin subset.
 *
 * Google Fonts returns one @font-face per (family, weight, style, subset). The subset
 * is identified by the comment preceding the block (e.g. `/* latin *\/`) and/or by
 * the unicode-range. We key on the comment because it's stable and cheap to parse.
 *
 * @param {string} css - Raw CSS text from the Google Fonts stylesheet endpoint
 * @returns {{
 *   fontFaces: Array<{
 *     subset: string,
 *     fontFamily: string,
 *     fontStyle: string,
 *     fontWeight: string,
 *     src: string,
 *     unicodeRange: string,
 *     block: string
 *   }>
 * }}
 */
export const parseGoogleFontsCss = css => {
  if (!css || typeof css !== 'string') return { fontFaces: [] };

  const fontFaces = [];
  // Match an optional `/* subset */` comment followed by a @font-face block.
  // Using a non-greedy match on the block body so nested braces can't bleed.
  const blockRegex = /(?:\/\*\s*([^*]+?)\s*\*\/\s*)?@font-face\s*\{([^}]*)\}/g;

  let match;
  while ((match = blockRegex.exec(css)) !== null) {
    const subset = (match[1] || '').trim();
    const body = match[2];

    const familyMatch = body.match(/font-family:\s*['"]?([^'";]+)['"]?\s*;/);
    const styleMatch = body.match(/font-style:\s*([^;]+);/);
    const weightMatch = body.match(/font-weight:\s*([^;]+);/);
    const srcMatch = body.match(/src:\s*url\(([^)]+)\)\s*format\(['"]?woff2['"]?\)/);
    const unicodeMatch = body.match(/unicode-range:\s*([^;]+);/);

    if (!familyMatch || !srcMatch) continue;

    fontFaces.push({
      subset,
      fontFamily: familyMatch[1].trim(),
      fontStyle: styleMatch ? styleMatch[1].trim() : 'normal',
      fontWeight: weightMatch ? weightMatch[1].trim() : '400',
      src: srcMatch[1].trim(),
      unicodeRange: unicodeMatch ? unicodeMatch[1].trim() : '',
      block: match[0],
    });
  }

  return { fontFaces };
};

/**
 * Validate if a font name appears to be a valid Google Font
 * @param {string} fontName - Font family name to validate
 * @returns {boolean} True if the font name appears valid for Google Fonts
 */
export const isValidGoogleFontName = fontName => {
  if (!fontName || typeof fontName !== 'string') return false;

  const trimmed = fontName.trim();

  // Basic validation rules
  if (trimmed.length === 0) return false;
  if (trimmed.length > 100) return false; // Reasonable length limit

  // Should not contain special characters that would break URLs
  const invalidChars = /[<>{}|\\^`\[\]]/;
  if (invalidChars.test(trimmed)) return false;

  // Should not be a system font
  const systemFonts = [
    'default',
    'inherit',
    'initial',
    'unset',
    'serif',
    'sans-serif',
    'monospace',
    'cursive',
    'fantasy',
  ];

  if (systemFonts.includes(trimmed.toLowerCase())) return false;

  return true;
};

/**
 * Normalize font weight to standard Google Fonts weights
 * @param {number|string} weight - Font weight value
 * @returns {number} Normalized weight (100-900)
 */
export const normalizeWeight = weight => {
  const numWeight = parseInt(weight, 10);

  if (isNaN(numWeight)) return 400; // Default to normal weight

  // Clamp to valid range
  if (numWeight < 100) return 100;
  if (numWeight > 900) return 900;

  // Round to nearest hundred
  return Math.round(numWeight / 100) * 100;
};

/**
 * Extract font specifications that need Google Fonts loading
 * @param {Object[]} allFonts - All font specifications
 * @returns {Object[]} Fonts that need to be loaded from Google Fonts
 */
export const filterGoogleFonts = (allFonts = []) => {
  if (!Array.isArray(allFonts)) return [];

  return allFonts.filter(font => {
    if (!font || !font.family) return false;

    const fontName = font.family.trim();

    // Skip only true system fonts - not Google Fonts that might be commonly used
    const systemFonts = [
      '-apple-system',
      'blinkmacsystemfont',
      'segoe ui',
      'helvetica neue',
      'sans-serif',
      'serif',
      'monospace',
      'cursive',
      'fantasy',
      'arial',
      'helvetica',
      'times',
      'times new roman',
      'courier',
      'courier new',
      'georgia',
      'verdana',
      'tahoma',
      'trebuchet ms',
      'comic sans ms',
      'impact',
      'palatino',
      'garamond',
      'bookman',
      'avant garde',
      'default',
    ];

    const lowerFontName = fontName.toLowerCase();

    // First check if it's a system font
    if (systemFonts.includes(lowerFontName)) {
      return false;
    }

    // For better performance, we now assume any non-system font could be a Google Font
    // The actual validation will happen when generating the URL with real API data
    return isValidGoogleFontName(fontName);
  });
};

/**
 * Generate CSS variables for custom fonts
 * @param {Object[]} fonts - Font specifications
 * @returns {string[]} Array of CSS variable declarations
 */
export const generateFontCssVariables = (fonts = []) => {
  const variables = [];

  fonts.forEach(font => {
    if (!font.family) return;

    const safeName = font.family
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/--+/g, '-')
      .replace(/^-|-$/g, '');

    const fontFamily = buildFontFamilyWithFallbacks(font.family);
    variables.push(`--cp_font-${safeName}: ${fontFamily};`);

    if (font.weights && font.weights.length > 0) {
      variables.push(`--cp_font-${safeName}-weights: ${font.weights.join(',')};`);
    }
  });

  return variables;
};

/**
 * Convert font size values to pixels for use in Stripe Elements
 * This ensures consistent sizing across iframe boundaries
 * @param {string} fontSize - Font size value that might be in rem
 * @returns {string} Font size in pixels
 */
export const convertFontSizeForStripe = fontSize => {
  if (typeof fontSize === 'number') {
    return `${fontSize}px`;
  }

  if (!fontSize || typeof fontSize !== 'string') {
    return '16px'; // Default fallback
  }

  // If it's already in pixels, return as-is
  if (fontSize.includes('px')) {
    return fontSize;
  }

  // If it's a bare number (no unit), treat as pixels
  if (/^\d+(\.\d+)?$/.test(fontSize)) {
    return `${fontSize}px`;
  }

  // If it's not a rem value, return the default
  if (!fontSize.includes('rem')) {
    return '16px';
  }

  // Use browser's default 16px as base to avoid circular calculation
  // (since our CSS sets html font-size using rem values)
  const browserDefaultFontSize = 16;

  // Extract the numeric value from the rem string
  const remNumericValue = parseFloat(fontSize.replace('rem', ''));

  if (isNaN(remNumericValue)) {
    return '16px';
  }

  // Convert to pixels using browser default
  const pixelValue = remNumericValue * browserDefaultFontSize;

  return `${Math.round(pixelValue)}px`;
};

export default {
  formatFontNameForUrl,
  generateFontFamilyParam,
  generateGoogleFontsUrl,
  buildFontFamilyWithFallbacks,
  getAdjustedFallbackName,
  parseGoogleFontsCss,
  isValidGoogleFontName,
  normalizeWeight,
  filterGoogleFonts,
  generateFontCssVariables,
};
